import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getRewards, createReward, updateReward, deleteReward } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Gift,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRewardsPage() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    points_required: '',
    discount_value: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const res = await getRewards();
      setRewards(res.data);
    } catch (error) {
      toast.error('Error al cargar recompensas');
    }
    setLoading(false);
  };

  const handleOpenModal = (reward = null) => {
    if (reward) {
      setEditingReward(reward);
      setFormData({
        name: reward.name,
        points_required: reward.points_required.toString(),
        discount_value: reward.discount_value?.toString() || '0',
        description: reward.description || ''
      });
    } else {
      setEditingReward(null);
      setFormData({
        name: '',
        points_required: '',
        discount_value: '',
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const data = {
      name: formData.name,
      points_required: parseInt(formData.points_required),
      discount_value: parseFloat(formData.discount_value) || 0,
      description: formData.description || null
    };
    
    try {
      if (editingReward) {
        await updateReward(editingReward.id, data);
        toast.success('Recompensa actualizada');
      } else {
        await createReward(data);
        toast.success('Recompensa creada');
      }
      setShowModal(false);
      fetchRewards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (reward) => {
    try {
      await updateReward(reward.id, { active: !reward.active });
      setRewards(rewards.map(r => 
        r.id === reward.id ? { ...r, active: !r.active } : r
      ));
      toast.success(reward.active ? 'Recompensa desactivada' : 'Recompensa activada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (reward) => {
    if (!window.confirm(`¿Eliminar "${reward.name}"?`)) return;
    
    try {
      await deleteReward(reward.id);
      setRewards(rewards.filter(r => r.id !== reward.id));
      toast.success('Recompensa eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Recompensas</h1>
            <p className="text-slate-600 mt-1">Configura las recompensas del programa de lealtad</p>
          </div>
          <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-reward-btn">
            <Plus className="w-5 h-5" />
            Nueva Recompensa
          </Button>
        </div>

        {/* Info Card */}
        <div className="card-base p-4 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <Star className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <div className="font-medium text-amber-800">Programa de Lealtad</div>
              <div className="text-sm text-amber-700 mt-1">
                Los clientes acumulan 1 punto por cada peso gastado. Los puntos pueden canjearse por las recompensas configuradas aquí.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rewards.map((reward) => (
            <div 
              key={reward.id}
              className={`card-base p-6 ${!reward.active ? 'opacity-60' : ''}`}
              data-testid={`reward-card-${reward.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Gift className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{reward.name}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-amber-600 font-bold">
                  <Star className="w-5 h-5" />
                  {reward.points_required}
                </div>
              </div>
              
              {reward.description && (
                <p className="text-sm text-slate-600 mb-2">{reward.description}</p>
              )}
              
              {reward.discount_value > 0 && (
                <div className="text-sm font-medium text-emerald-600 mb-4">
                  Valor: {formatCurrency(reward.discount_value)} de descuento
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={reward.active}
                    onCheckedChange={() => handleToggleActive(reward)}
                    data-testid={`reward-toggle-${reward.id}`}
                  />
                  <span className="text-sm text-slate-600">
                    {reward.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenModal(reward)}
                    data-testid={`edit-reward-${reward.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(reward)}
                    data-testid={`delete-reward-${reward.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingReward ? 'Editar Recompensa' : 'Nueva Recompensa'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Lavado Básico Gratis"
                required
                className="mt-1"
                data-testid="reward-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="points">Puntos requeridos *</Label>
              <div className="relative mt-1">
                <Star className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="points"
                  type="number"
                  min="1"
                  value={formData.points_required}
                  onChange={(e) => setFormData({ ...formData, points_required: e.target.value })}
                  placeholder="500"
                  required
                  className="pl-10"
                  data-testid="reward-points-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="discount_value">Valor del descuento (MXN) *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <Input
                  id="discount_value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder="100"
                  required
                  className="pl-8"
                  data-testid="reward-discount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Monto que se descuenta del total al canjear</p>
            </div>
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la recompensa..."
                className="mt-1"
                data-testid="reward-description-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={submitting}
              data-testid="save-reward-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
